import type { CameraConnectionIssueReason } from 'src/services/posthogEvents';
import { StateCreator } from 'zustand';

import { AutoConnectCameraStatus } from 'src/models/Camera.model';
import { CameraData } from 'src/models/CameraData.model';
import { BackendStatusEnum } from 'src/models/GalenData/DeviceDataView.model';

import { RemoteBoundStoreState } from './useRemoteBoundStore';

interface StateTypes {
  isCapturing: boolean;
  isCanceling: boolean;
  leftUrl: string;
  rightUrl: string;
  cameraData: CameraData | undefined;
  backendStatus: BackendStatusEnum | undefined;
  autoConnectCameraStatus: AutoConnectCameraStatus;
  hasStarted: boolean;
  leftImageAttempts: number;
  rightImageAttempts: number;
  leftImageTaken: boolean;
  rightImageTaken: boolean;
  leftImageSubmission: number;
  rightImageSubmission: number;
  cameraConnectionIssueReason: CameraConnectionIssueReason | undefined;
  captureInterruptedByConnectionIssue: boolean;
}
type ActionsTypes = {
  setIsCapturing: (isCapturing: boolean) => void;
  setIsCanceling: (isCanceling: boolean) => void;
  setLeftUrl: (leftUrl: string) => void;
  setRightUrl: (rightUrl: string) => void;
  setCameraData: (cameraData: CameraData | undefined) => void;
  setBackendStatus: (backendStatus: BackendStatusEnum | undefined) => void;
  setAutoConnectCameraStatus: (status: AutoConnectCameraStatus) => void;
  setHasStarted: (hasStarted: boolean) => void;
  setLeftImageAttempts: (leftImageAttempts: number) => void;
  setRightImageAttempts: (rightImageAttempts: number) => void;
  incrementLeftImageAttempts: () => void;
  incrementRightImageAttempts: () => void;
  setLeftImageTaken: (leftImageTaken: boolean) => void;
  setRightImageTaken: (rightImageTaken: boolean) => void;
  setLeftImageSubmission: (leftImageSubmission: number) => void;
  setRightImageSubmission: (rightImageSubmission: number) => void;
  incrementLeftImageSubmission: () => void;
  incrementRightImageSubmission: () => void;
  setCameraConnectionIssueReason: (
    reason: CameraConnectionIssueReason | undefined,
  ) => void;
  setCaptureInterruptedByConnectionIssue: (interrupted: boolean) => void;
};

export type CameraSliceTypes = StateTypes & ActionsTypes;

export const createCameraSlice: StateCreator<
  RemoteBoundStoreState,
  [],
  [],
  CameraSliceTypes
> = (set, get) => ({
  isCapturing: false,
  isCanceling: false,
  leftUrl: '',
  rightUrl: '',
  cameraData: undefined,
  backendStatus: undefined,
  autoConnectCameraStatus: AutoConnectCameraStatus.FAILED,
  hasStarted: false,
  leftImageAttempts: 0,
  rightImageAttempts: 0,
  leftImageTaken: false,
  rightImageTaken: false,
  leftImageSubmission: 0,
  rightImageSubmission: 0,
  cameraConnectionIssueReason: undefined,
  captureInterruptedByConnectionIssue: false,
  setIsCapturing: (isCapturing: boolean) => set({ isCapturing }),
  setIsCanceling: (isCanceling: boolean) => set({ isCanceling }),
  setLeftUrl: (leftUrl: string) => set({ leftUrl }),
  setRightUrl: (rightUrl: string) => set({ rightUrl }),
  setCameraData: (cameraData: CameraData | undefined) => set({ cameraData }),
  setBackendStatus: (backendStatus: BackendStatusEnum | undefined) =>
    set({ backendStatus }),
  setAutoConnectCameraStatus: (status: AutoConnectCameraStatus) =>
    set({ autoConnectCameraStatus: status }),
  setHasStarted: (hasStarted: boolean) => set({ hasStarted }),
  setLeftImageAttempts: (leftImageAttempts: number) =>
    set({ leftImageAttempts }),
  setRightImageAttempts: (rightImageAttempts: number) =>
    set({ rightImageAttempts }),
  incrementLeftImageAttempts: () =>
    set((state) => ({
      leftImageAttempts: state.leftImageAttempts + 1,
      leftImageTaken: true,
    })),
  incrementRightImageAttempts: () =>
    set((state) => ({
      rightImageAttempts: state.rightImageAttempts + 1,
      rightImageTaken: true,
    })),
  setLeftImageTaken: (leftImageTaken: boolean) => set({ leftImageTaken }),
  setRightImageTaken: (rightImageTaken: boolean) => set({ rightImageTaken }),
  setLeftImageSubmission: (leftImageSubmission: number) =>
    set({ leftImageSubmission }),
  setRightImageSubmission: (rightImageSubmission: number) =>
    set({ rightImageSubmission }),
  incrementLeftImageSubmission: () =>
    set((state) => ({ leftImageSubmission: state.leftImageSubmission + 1 })),
  incrementRightImageSubmission: () =>
    set((state) => ({ rightImageSubmission: state.rightImageSubmission + 1 })),
  setCameraConnectionIssueReason: (reason) => {
    if (!reason) {
      set({ cameraConnectionIssueReason: undefined });
      return;
    }

    const wasCapturing = get().isCapturing;
    set({
      cameraConnectionIssueReason: reason,
      isCapturing: false,
      isCanceling: false,
      hasStarted: false,
      ...(wasCapturing ? { captureInterruptedByConnectionIssue: true } : {}),
    });
  },
  setCaptureInterruptedByConnectionIssue: (interrupted) =>
    set({ captureInterruptedByConnectionIssue: interrupted }),
});
